/**
 * platformDetect — helpers de detección de plataforma/browser.
 *
 * Usado por MobileWelcomeWizard para decidir qué instrucción de
 * "agregar a pantalla de inicio" mostrar (varía según browser y SO),
 * y para evitar mostrar el wizard si la app ya está instalada
 * (display-mode: standalone).
 *
 * Cero dependencias. Pasa al cliente como JS muy chico.
 */

export interface PlatformInfo {
  isMobile: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  isSafari: boolean;
  isChrome: boolean;
  isFirefox: boolean;
  isEdge: boolean;
  /** App ya instalada (PWA standalone). Cuando es true, NO mostrar wizard. */
  isStandalone: boolean;
  /** Soporta Web Push (Service Worker + PushManager + Notification). */
  supportsPush: boolean;
  /** Browser ofrecerá `beforeinstallprompt` (Chrome/Edge desktop+Android, NO Safari). */
  canPromptInstall: boolean;
}

export function detectPlatform(): PlatformInfo {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return {
      isMobile: false,
      isIOS: false,
      isAndroid: false,
      isSafari: false,
      isChrome: false,
      isFirefox: false,
      isEdge: false,
      isStandalone: false,
      supportsPush: false,
      canPromptInstall: false,
    };
  }
  const ua = navigator.userAgent || '';
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/i.test(ua);
  const isMobile =
    isIOS ||
    isAndroid ||
    /Mobi|Opera Mini/i.test(ua) ||
    window.matchMedia('(max-width: 767px)').matches;

  const isEdge = /Edg\//i.test(ua);
  const isChrome = !isEdge && /Chrome|CriOS/i.test(ua);
  const isFirefox = /Firefox|FxiOS/i.test(ua);
  // Safari es un poco tricky: Chrome iOS también pone "Safari" en su UA,
  // por eso primero descartamos Chrome/Edge/Firefox.
  const isSafari = !isChrome && !isEdge && !isFirefox && /Safari/i.test(ua);

  // PWA standalone: en iOS está en navigator.standalone, en Android via matchMedia.
  const standaloneByMedia =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;
  const standaloneIos =
    (window.navigator as { standalone?: boolean }).standalone === true;
  const isStandalone = standaloneByMedia || standaloneIos;

  const supportsPush =
    typeof Notification !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window;

  // beforeinstallprompt: Chrome/Edge/Opera lo soportan. Safari/iOS NO.
  // Firefox Android tampoco (a la fecha 2026).
  const canPromptInstall = (isChrome || isEdge) && !isIOS;

  return {
    isMobile,
    isIOS,
    isAndroid,
    isSafari,
    isChrome,
    isFirefox,
    isEdge,
    isStandalone,
    supportsPush,
    canPromptInstall,
  };
}
